/**
 * Speech Expressiveness Layer
 * Adds subtle brow, eye, and cheek modulation during speech to make
 * avatar look more expressive and natural rather than just moving lips.
 * Runs as an additive overlay on top of viseme lip-sync.
 */

import { resolveMorphTargetIndex } from '../converter';

interface ExpressionChannel {
  morphName: string;
  frequency: number;
  amplitude: number;
  phase: number;
  resolvedIdx?: number;
}

const EXPRESSION_CHANNELS: ExpressionChannel[] = [
  // Brows rise slightly during emphasis/questions
  { morphName: 'browInnerUp', frequency: 0.8, amplitude: 0.08, phase: 0 },
  { morphName: 'browOuterUpLeft', frequency: 0.6, amplitude: 0.05, phase: 0.3 },
  { morphName: 'browOuterUpRight', frequency: 0.6, amplitude: 0.05, phase: 0.5 },
  // Cheek squint adds warmth during speech
  { morphName: 'cheekSquintLeft', frequency: 0.4, amplitude: 0.04, phase: 1.2 },
  { morphName: 'cheekSquintRight', frequency: 0.4, amplitude: 0.04, phase: 1.4 },
  // Subtle eye squint during emphasis
  { morphName: 'eyeSquintLeft', frequency: 0.5, amplitude: 0.03, phase: 0.8 },
  { morphName: 'eyeSquintRight', frequency: 0.5, amplitude: 0.03, phase: 1.0 },
];

export class SpeechExpressivenessController {
  private channels: ExpressionChannel[];
  private isSpeaking = false;
  private fadeTarget = 0;
  private fadeCurrent = 0;
  private seed: number;
  private mesh: THREE.SkinnedMesh | null = null;

  constructor(morphDict: Record<string, number> | null) {
    this.seed = Math.random() * Math.PI * 2;
    this.channels = EXPRESSION_CHANNELS.map(ch => {
      const idx = morphDict ? resolveMorphTargetIndex(morphDict, ch.morphName) : undefined;
      return { ...ch, resolvedIdx: idx };
    }).filter(ch => ch.resolvedIdx !== undefined);
  }

  setMesh(mesh: THREE.SkinnedMesh | null): void {
    this.mesh = mesh;
  }

  setSpeaking(speaking: boolean): void {
    this.isSpeaking = speaking;
    this.fadeTarget = speaking ? 1 : 0;
  }

  update(timeMs: number): void {
    if (this.channels.length === 0 || !this.mesh?.morphTargetInfluences) return;

    // Smooth fade in/out
    const fadeSpeed = this.isSpeaking ? 0.08 : 0.04;
    this.fadeCurrent += (this.fadeTarget - this.fadeCurrent) * fadeSpeed;

    if (this.fadeCurrent < 0.001) return;

    const t = timeMs / 1000;
    const influences = this.mesh.morphTargetInfluences;

    for (const ch of this.channels) {
      if (ch.resolvedIdx === undefined || ch.resolvedIdx >= influences.length) continue;
      const wave = Math.sin(t * ch.frequency * Math.PI * 2 + ch.phase + this.seed);
      const value = Math.max(0, wave * ch.amplitude * this.fadeCurrent);
      influences[ch.resolvedIdx] = Math.min(1, influences[ch.resolvedIdx] + value);
    }
  }
}
